import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';

export type PlanType = 'free' | 'premium' | 'elite' | null;
export type SportPreference = 'football' | 'tennis' | 'basketball' | null;

interface AuthUser {
  username: string;
  email: string;
  nombre: string;
}

interface AuthContextType {
  isRegistered: boolean;
  isPremium: boolean;
  isAdmin: boolean;
  plan: PlanType;
  sportPreference: SportPreference;
  user: AuthUser | null;
  avatarUrl: string | null;
  supabaseUser: User | null;
  loading: boolean;
  registerModalOpen: boolean;
  loginModalOpen: boolean;
  paywallModalOpen: boolean;
  paywallTargetPlan: 'premium' | 'elite' | null;
  openRegisterModal: () => void;
  closeRegisterModal: () => void;
  openLoginModal: () => void;
  closeLoginModal: () => void;
  openPaywallModal: (targetPlan?: 'premium' | 'elite') => void;
  closePaywallModal: () => void;
  setPlan: (plan: PlanType) => void;
  setSportPreference: (sport: SportPreference) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [plan, setPlanState] = useState<PlanType>(null);
  const [sportPreference, setSportPreferenceState] = useState<SportPreference>(null);
  const [isAdminState, setIsAdminState] = useState(false);
  const [loading, setLoading] = useState(true);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [paywallTargetPlan, setPaywallTargetPlan] = useState<'premium' | 'elite' | null>(null);

  const fetchProfile = async (uid: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('username, nombre, plan, avatar_url, sport, is_admin')
      .eq('id', uid)
      .maybeSingle();

    if (data) {
      setUser({ username: data.username, email: '', nombre: data.nombre });
      setPlanState(data.plan as PlanType);
      setAvatarUrl(data.avatar_url ?? null);
      setSportPreferenceState((data.sport as SportPreference) ?? null);
      setIsAdminState(data.is_admin === true);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setSupabaseUser(currentSession?.user ?? null);
      if (currentSession?.user) {
        fetchProfile(currentSession.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setSupabaseUser(newSession?.user ?? null);
      if (newSession?.user) {
        fetchProfile(newSession.user.id);
      } else {
        setUser(null);
        setPlanState(null);
        setAvatarUrl(null);
        setIsAdminState(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const refreshProfile = async () => {
    if (supabaseUser) {
      await fetchProfile(supabaseUser.id);
    }
  };

  const isRegistered = !!supabaseUser && !!session;
  const isPremium = isAdminState || plan === 'premium' || plan === 'elite';

  const setPlan = async (newPlan: PlanType) => {
    if (!supabaseUser || !newPlan) return;
    setPlanState(newPlan);
    await supabase.from('profiles').update({ plan: newPlan }).eq('id', supabaseUser.id);
  };

  const setSportPreference = async (sport: SportPreference) => {
    if (!supabaseUser) return;
    setSportPreferenceState(sport);
    await supabase.from('profiles').update({ sport }).eq('id', supabaseUser.id);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setPlanState(null);
    setAvatarUrl(null);
    setIsAdminState(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isRegistered,
        isPremium,
        isAdmin: isAdminState,
        plan,
        sportPreference,
        user,
        avatarUrl,
        supabaseUser,
        loading,
        registerModalOpen,
        loginModalOpen,
        paywallModalOpen,
        paywallTargetPlan,
        openRegisterModal: () => { setLoginModalOpen(false); setRegisterModalOpen(true); },
        closeRegisterModal: () => setRegisterModalOpen(false),
        openLoginModal: () => { setRegisterModalOpen(false); setLoginModalOpen(true); },
        closeLoginModal: () => setLoginModalOpen(false),
        openPaywallModal: (targetPlan?: 'premium' | 'elite') => {
          setPaywallTargetPlan(targetPlan ?? null);
          setPaywallModalOpen(true);
        },
        closePaywallModal: () => { setPaywallModalOpen(false); setPaywallTargetPlan(null); },
        setPlan,
        setSportPreference,
        logout,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
